import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiService from '../../service/ApiService';
import { getEnvVar } from '../../envUtils';
import { showToastMessage } from '../../utils/commonFunctions';
import {
    YML_EXTENSIONS,
    NAVIGATION_DELAY_SUCCESS,
    NAVIGATION_DELAY_ERROR,
    DEFAULT_FILENAME
} from '../../utils/constants';

interface UserData {
    url?: string;
}

const YmlFileHandler: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    /**
     * Navigates to the default route based on user details in sessionStorage
     */
    const navigateToDefaultRoute = useCallback(() => {
        try {
            const userDetails = sessionStorage.getItem('userDetails');
            const publicUrl = process.env.PUBLIC_URL || '';
            
            if (userDetails) {
                const userData: UserData = JSON.parse(userDetails);
                const userUrl = userData?.url || '/user';
                navigate(`${publicUrl}${userUrl}`);
            } else {
                navigate(`${publicUrl}/`);
            }
        } catch (error) {
            console.error('Error parsing user details:', error);
            navigate(`${process.env.PUBLIC_URL || ''}/`);
        }
    }, [navigate]);

    /**
     * Normalizes the backend URL by removing trailing slashes and /api suffix
     */
    const normalizeBackendUrl = useCallback((url: string): string => {
        let normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        
        if (normalizedUrl.endsWith('/api')) {
            normalizedUrl = normalizedUrl.slice(0, -4);
        }
        
        return normalizedUrl;
    }, []);

    /**
     * Checks if the file path has a YML extension
     */
    const isYmlFile = useCallback((filePath: string): boolean => {
        return YML_EXTENSIONS.some(ext => filePath.endsWith(ext));
    }, []);

    /**
     * Extracts filename from file path
     */
    const extractFileName = useCallback((filePath: string): string => {
        return filePath.split('/').pop() || DEFAULT_FILENAME;
    }, []);

    /**
     * Triggers browser download for the blob
     */
    const downloadBlob = useCallback((blob: Blob, fileName: string): void => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    /**
     * Extracts error message from error object
     */
    const getErrorMessage = useCallback((error: unknown): string => {
        if (error && typeof error === 'object') {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return err?.response?.data?.message || err?.message || 'Failed to fetch file';
        }
        return 'Failed to fetch file';
    }, []);

    useEffect(() => {
        const fetchAndDownloadFile = async () => {
            try {
                // Extract file path from URL (e.g., /openapi.yml)
                const filePath = location.pathname.replace(process.env.PUBLIC_URL || '', '');
                
                // Check if path ends with .yml or .yaml
                if (!isYmlFile(filePath)) {
                    navigateToDefaultRoute();
                    return;
                }

                const fileName = extractFileName(filePath);

                // Show loading toast message
                showToastMessage('info', `Downloading ${fileName}...`);

                // Get and normalize backend base URL
                const backendBaseUrl = getEnvVar('REACT_APP_BACKEND_BASE_URL');
                const normalizedBaseUrl = normalizeBackendUrl(backendBaseUrl);
                const fileUrl = `${normalizedBaseUrl}${filePath}`;

                // Fetch file as blob
                const response = await ApiService.getBlob(fileUrl, {}, null);
                
                if (response?.data) {
                    // Trigger download directly from blob
                    downloadBlob(response.data, fileName);

                    // Show success toast message
                    showToastMessage('success', `${fileName} downloaded successfully`);

                    // Navigate back after download
                    setTimeout(() => {
                        navigateToDefaultRoute();
                    }, NAVIGATION_DELAY_SUCCESS);
                } else {
                    throw new Error('No file content received');
                }
            } catch (error) {
                console.error('Error fetching file:', error);
                const errorMessage = getErrorMessage(error);
                
                // Show error toast message
                showToastMessage('error', errorMessage);
                
                // Navigate back after showing error
                setTimeout(() => {
                    navigateToDefaultRoute();
                }, NAVIGATION_DELAY_ERROR);
            }
        };

        fetchAndDownloadFile();
    }, [location.pathname, navigate, isYmlFile, navigateToDefaultRoute, normalizeBackendUrl, extractFileName, downloadBlob, getErrorMessage]);

    // Don't render anything - all feedback is shown via toast messages
    return null;
};

export default YmlFileHandler;